import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useToast } from '@/components/ui/toast';
import { describeFailure } from '@/lib/i18n/errors';
import type { MessageKey } from '@/lib/i18n';
import { labels as labelsApi } from '@/lib/api/endpoints';
import type { LabelColor } from '@/lib/api/types';
import { keys } from '@/lib/queryClient';

export function useLabels() {
  return useQuery({
    queryKey: keys.labels,
    queryFn: () => labelsApi.list(),
    // Counts change whenever a task does, but not so fast that a stale badge
    // for a few seconds matters.
    staleTime: 15_000,
  });
}

function useLabelMutation<TVariables, TData>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  failureMessage: MessageKey,
) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.labels });
      // Task rows carry their labels, so they are stale too.
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error) => {
      toast.error(describeFailure(error, failureMessage));
    },
  });
}

export function useCreateLabel() {
  return useLabelMutation(
    (input: { name: string; color?: LabelColor }) => labelsApi.create(input),
    'error.labelCreate',
  );
}

export function useUpdateLabel() {
  return useLabelMutation(
    ({ id, ...body }: { id: string; name?: string; color?: LabelColor }) =>
      labelsApi.update(id, body),
    'error.labelUpdate',
  );
}

export function useDeleteLabel() {
  return useLabelMutation((id: string) => labelsApi.remove(id), 'error.labelDelete');
}

/** Replace your labels on one task. */
export function useSetTaskLabels(taskId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (labelIds: string[]) => labelsApi.setOnTask(taskId, labelIds),
    onSuccess: (updated) => {
      queryClient.setQueryData(keys.task(taskId), updated);
      void queryClient.invalidateQueries({ queryKey: keys.labels });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error) => {
      toast.error(describeFailure(error, 'error.labelAssign'));
    },
  });
}
